import CheckmarkIconOrange from "@/assets/images/icons/checkmark_icon_orange.svg";
import { LevelIndicatorBars } from "@/components/ui/LevelIndicatorBars";
import { CourseSkillLevel } from "@/lib/constants";
import {
  getLessonStructuredTitleMeta,
  LESSON_META_COLOR,
} from "@/utils/lessonStructuredTitle";
import React from "react";
import { Platform, Pressable, Text, View, type TextStyle } from "react-native";
import { Image, SizableText, useMedia, XStack, YStack } from "tamagui";
import { CardTitle } from "@/components/ui/Typography";
import {
  LEVEL_BAR_GRAY,
  LEVEL_INDICATOR_ACTIVE,
  SEARCH_BADGE_COLLECTION_BG,
  SEARCH_BADGE_COURSE_BG,
  SEARCH_BADGE_LESSON_BG,
  SEARCH_RESULT_BADGE_BASE,
  SEARCH_RESULT_L1_SHADOW,
  SEARCH_RESULT_THUMB_ASPECT,
} from "./globalSearchConstants";

export type GlobalSearchResultRowItem = {
  id: string;
  type: "course" | "lesson" | "collection";
  title: string;
  short_title?: string | null;
  summary: string;
  thumbnailUrl: string;
  slug: string;
  courseSlug?: string | null;
  courseTitle?: string | null;
  week_number?: number | null;
  main_work?: number | null;
  main_work_title?: string | null;
  skillLevel: CourseSkillLevel | null;
  progressPercentage?: number;
};

type GlobalSearchResultRowProps = {
  item: GlobalSearchResultRowItem;
  onPress: () => void;
  thumbColWidth: number;
  isSmUp: boolean;
};

export function GlobalSearchResultRow({
  item,
  onPress,
  thumbColWidth,
  isSmUp,
}: GlobalSearchResultRowProps) {
  const media = useMedia();
  const progressBarHeight = media.gtMd ? 6 : media.gtSm ? 4 : 2;
  const progress = item.progressPercentage ?? 0;
  const showCompletedCheck =
    progress >= 100 && (item.type === "lesson" || item.type === "course");
  const showThumbProgressBar = item.type !== "collection";
  const badgeBase = SEARCH_RESULT_BADGE_BASE as TextStyle;
  const parentCourseLabel =
    item.type !== "course" && item.courseTitle?.trim()
      ? item.courseTitle.trim()
      : null;
  const { displayTitle, metaLine, mainWorkTitle, weekLabel } =
    getLessonStructuredTitleMeta(item, {
      weekFormat: isSmUp ? "long" : "short",
    });
  const showStructuredLessonMeta = item.type === "lesson" && !!metaLine;
  const titleText = item.type === "lesson" ? displayTitle : item.title;
  const titleLineCount =
    isSmUp || (item.type === "lesson" && !showStructuredLessonMeta) ? 2 : 1;
  const showTopRow = isSmUp || showStructuredLessonMeta;
  // on desktop without structured meta, title lives in the top row; otherwise it gets its own row
  const showTitleRow = showStructuredLessonMeta || !isSmUp;

  const titleSizableText = (
    <CardTitle
      minWidth={0}
      color="#0D0D0D"
      numberOfLines={titleLineCount}
      ellipsizeMode="tail"
      overflow="hidden"
      style={
        Platform.OS === "web"
          ? {
              display: "-webkit-box",
              WebkitBoxOrient: "vertical" as const,
              WebkitLineClamp: titleLineCount,
              textOverflow: "ellipsis",
            }
          : undefined
      }
    >
      {titleText}
    </CardTitle>
  );

  const typeBadge = (
    <Text
      style={{
        ...badgeBase,
        backgroundColor:
          item.type === "course"
            ? SEARCH_BADGE_COURSE_BG
            : item.type === "collection"
              ? SEARCH_BADGE_COLLECTION_BG
              : SEARCH_BADGE_LESSON_BG,
      }}
    >
      {item.type === "course"
        ? "Course"
        : item.type === "collection"
          ? "Collection"
          : "Lesson"}
    </Text>
  );

  const resultMetaRow = (
    <XStack ai="center" gap={6} minWidth={0} flexShrink={1}>
      {item.skillLevel != null ? (
        <View
          style={{
            backgroundColor: SEARCH_BADGE_COURSE_BG,
            paddingHorizontal: 4,
            height: 16,
            borderRadius: 4,
            justifyContent: "center",
            alignItems: "center",
            flexShrink: 0,
            overflow: "hidden",
          }}
        >
          <LevelIndicatorBars
            level={item.skillLevel}
            activeColor={LEVEL_INDICATOR_ACTIVE}
            inactiveColor={LEVEL_BAR_GRAY}
            size={12}
          />
        </View>
      ) : null}
      {parentCourseLabel != null ? (
        <Text
          style={{
            ...badgeBase,
            backgroundColor: SEARCH_BADGE_COURSE_BG,
            color: LESSON_META_COLOR,
            fontSize: 12,
            fontWeight: "600",
            textTransform: "capitalize",
            flexShrink: 1,
            ...(Platform.OS === "android" ? { includeFontPadding: false } : {}),
          }}
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {parentCourseLabel}
        </Text>
      ) : null}
      <YStack flexShrink={0}>{typeBadge}</YStack>
    </XStack>
  );

  return (
    <Pressable onPress={onPress}>
      <XStack
        width="100%"
        ai="stretch"
        jc="flex-start"
        br={6}
        bg="#FFF"
        overflow="hidden"
        padding={0}
        style={SEARCH_RESULT_L1_SHADOW}
      >
        <YStack
          width={thumbColWidth}
          overflow="hidden"
          borderTopRightRadius={6}
          borderBottomRightRadius={6}
          bg="#eee"
          alignSelf="stretch"
        >
          <YStack
            position="relative"
            w="100%"
            overflow="hidden"
            borderTopRightRadius={6}
          >
            {item.thumbnailUrl ? (
              <Image
                source={{ uri: item.thumbnailUrl }}
                w="100%"
                aspectRatio={SEARCH_RESULT_THUMB_ASPECT}
                resizeMode="cover"
              />
            ) : (
              <YStack
                w="100%"
                aspectRatio={SEARCH_RESULT_THUMB_ASPECT}
                bg="#b0d0ff"
              />
            )}
            {showCompletedCheck ? (
              <YStack
                position="absolute"
                top={9}
                right={9}
                jc="center"
                ai="center"
                zIndex={1}
              >
                <CheckmarkIconOrange width={16} height={16} />
              </YStack>
            ) : null}
          </YStack>
          {showThumbProgressBar ? (
            <XStack
              h={progressBarHeight}
              backgroundColor="#D2CCC5"
              overflow="hidden"
            >
              <YStack
                h="100%"
                backgroundColor="$accent"
                borderRadius={30}
                w={`${progress}%`}
              />
            </XStack>
          ) : null}
        </YStack>

        <YStack
          flex={1}
          gap={2}
          minWidth={0}
          jc="center"
          p={8}
          $gtSm={{ p: 16, gap: 4 }}
        >
          {showTopRow ? (
            <XStack
              width="100%"
              minWidth={0}
              gap={8}
              ai="center"
              jc="space-between"
            >
              <YStack flex={1} minWidth={0} jc="center">
                {showStructuredLessonMeta ? (
                  mainWorkTitle && weekLabel ? (
                    <XStack alignItems="center" flexWrap="nowrap" width="100%">
                      <SizableText
                        minWidth={0}
                        color={LESSON_META_COLOR}
                        fontSize={isSmUp ? 12 : 10}
                        lineHeight={16}
                        fontWeight="600"
                        numberOfLines={1}
                        ellipsizeMode="tail"
                        overflow="hidden"
                        flexShrink={1}
                      >
                        {mainWorkTitle}
                      </SizableText>
                      <SizableText
                        color={LESSON_META_COLOR}
                        fontSize={isSmUp ? 12 : 10}
                        lineHeight={16}
                        fontWeight="600"
                        flexShrink={0}
                      >
                        {` - ${weekLabel}`}
                      </SizableText>
                    </XStack>
                  ) : (
                    <SizableText
                      minWidth={0}
                      color={LESSON_META_COLOR}
                      fontSize={isSmUp ? 12 : 10}
                      lineHeight={16}
                      fontWeight="600"
                      numberOfLines={1}
                      ellipsizeMode="tail"
                      overflow="hidden"
                    >
                      {metaLine}
                    </SizableText>
                  )
                ) : (
                  titleSizableText
                )}
              </YStack>
              {isSmUp ? resultMetaRow : null}
            </XStack>
          ) : null}
          {showTitleRow ? titleSizableText : null}
          {!isSmUp ? <YStack mt={2}>{resultMetaRow}</YStack> : null}
          {item.summary && isSmUp ? (
            <Text
              style={{
                width: "100%",
                color: "#0D0D0D",
                fontSize: isSmUp ? 14 : 12,
                lineHeight: isSmUp ? 18 : 16,
                fontWeight: "400",
                marginTop: 2,
                ...(Platform.OS === "android"
                  ? { includeFontPadding: false }
                  : {}),
              }}
              numberOfLines={isSmUp ? 2 : 1}
              ellipsizeMode="tail"
            >
              {item.summary}
            </Text>
          ) : null}
        </YStack>
      </XStack>
    </Pressable>
  );
}
